import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useField } from "react-final-form";
import { TVerificationAppFieldConfigItem } from "../../../app/models";
import { ConfirmationDialog } from "../../../ui/components/ConfirmationDialog";
import { TDocumentData, TDocumentInfo } from "../../models";
import {
  FILES_NAMESPACE,
  TActiveFileIntent,
  TFileIntent,
  TKeepFileIntent,
  TUploadFileIntent,
  getActiveFileIntents,
  hasSelectedFileKind,
  normalizeFileIntents,
} from "../../utils/fileIntents";
import { getFilesForField } from "../../utils/fileFieldMatching";
import { FileContent } from "../FileContent";
import { mainTheme } from "../../../../theme/mainTheme";

type NamedFieldConfig = TVerificationAppFieldConfigItem & { name: string };

export const FILE_KIND_REQUIRED_ERROR =
  "Select a document type for every file";

type Props = {
  field: NamedFieldConfig;
  disabled?: boolean;
  fileItems?: TDocumentData[];
  requestId?: string;
  documentInfo?: TDocumentInfo;
};

type ReadOnlyProps = {
  field: NamedFieldConfig;
  fileItems?: TDocumentData[];
  requestId?: string;
  documentInfo?: TDocumentInfo;
};

type EditableProps = {
  field: NamedFieldConfig;
  requestId?: string;
  documentInfo?: TDocumentInfo;
};

const ACCEPT = ".jpeg,.jpg,.png,.bmp,.pdf,.doc,.docx";
const DELETE_CONFIRM_TITLE = "Delete file?";
const DELETE_CONFIRM_MESSAGE = "Are you sure you want to delete the file?";
const FILE_KIND_LABEL = "Document type";

const isMultipleField = (field: NamedFieldConfig): boolean =>
  field.multiple === true;

const wrapValue = (
  isMultiple: boolean,
  intents: TFileIntent[],
): TFileIntent | TFileIntent[] => {
  if (isMultiple) return intents;
  return intents[0] ?? { mode: "empty" };
};

function DeleteFileButton(props: { onDelete: () => void; testId: string }) {
  const { onDelete, testId } = props;
  return (
    <Tooltip title="Delete file">
      <IconButton
        aria-label="Delete file"
        data-testid={testId}
        color="error"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: "background.paper",
          boxShadow: 1,
          color: "error.main",
          "&:hover": {
            bgcolor: "rgba(211, 47, 47, 0.08)",
          },
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function PendingUploadPreview(props: { fileName: string }) {
  return (
    <Box
      sx={{
        width: 280,
        height: 280,
        border: "1px dashed #1976d2",
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        textAlign: "center",
        padding: 1,
      }}
    >
      <Stack alignItems="center" spacing={0.5}>
        <Typography variant="body2">Pending upload</Typography>
        <Typography variant="caption">{props.fileName}</Typography>
      </Stack>
    </Box>
  );
}

export const FilePickerField = memo((props: Props) => {
  const { field, disabled, fileItems, requestId, documentInfo } = props;
  if (disabled) {
    return (
      <ReadOnlyFilePickerField
        field={field}
        fileItems={fileItems}
        requestId={requestId}
        documentInfo={documentInfo}
      />
    );
  }

  return (
    <EditableFilePickerField
      field={field}
      requestId={requestId}
      documentInfo={documentInfo}
    />
  );
});

function ReadOnlyFilePickerField(props: ReadOnlyProps) {
  const { field, fileItems, requestId, documentInfo } = props;
  const items = getFilesForField(field.name, fileItems ?? []);
  const canShowThumbnails = requestId && documentInfo && items.length > 0;

  return (
    <Box sx={{ gridColumn: "1 / -1", marginBottom: 2 }}>
      {canShowThumbnails && (
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {items.map((dataItem) => (
            <Stack key={dataItem.dataId} gap={1}>
              <FileContent
                requestId={requestId}
                documentId={documentInfo.documentId}
                fileId={dataItem.dataId}
                dataLink={dataItem.dataLink}
                fileInfo={dataItem.fileInfo}
              />
              {dataItem.fileInfo?.kind && (
                <Typography variant="body2" color="text.secondary">
                  {dataItem.fileInfo.kind}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

const EditableFilePickerField = memo((props: EditableProps) => {
  const { field, requestId, documentInfo } = props;
  const isMultiple = isMultipleField(field);
  const kinds = field.kinds && field.kinds.length > 0 ? field.kinds : null;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<TFileIntent | null>(null);

  const validate = useCallback(
    (value: TFileIntent | TFileIntent[] | undefined) => {
      if (!kinds) return undefined;
      const active = getActiveFileIntents(value);
      if (active.some((intent) => !hasSelectedFileKind(intent))) {
        return FILE_KIND_REQUIRED_ERROR;
      }
      return undefined;
    },
    [kinds],
  );

  const { input, meta } = useField<TFileIntent | TFileIntent[] | undefined>(
    `${FILES_NAMESPACE}.${field.name}`,
    { subscription: { value: true, error: true }, validate },
  );

  const intents = useMemo(
    () => normalizeFileIntents(input.value),
    [input.value],
  );

  const setIntents = useCallback(
    (next: TFileIntent[]) => {
      input.onChange(wrapValue(isMultiple, next));
      input.onBlur();
    },
    [input, isMultiple],
  );

  const handleAddClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const newIntents: TUploadFileIntent[] = Array.from(files).map((file) => ({
        mode: "upload",
        file,
      }));

      if (isMultiple) {
        setIntents([...intents, ...newIntents]);
      } else {
        const next = [...intents];
        const replaceIdx = next.findIndex(
          (intent) => intent.mode === "keep" || intent.mode === "upload",
        );
        if (replaceIdx === -1) {
          setIntents([...intents, newIntents[0]]);
        } else if (next[replaceIdx].mode === "keep") {
          const replaced = next[replaceIdx] as TKeepFileIntent;
          next[replaceIdx] = { ...replaced, mode: "delete" };
          next.splice(replaceIdx + 1, 0, newIntents[0]);
          setIntents(next);
        } else {
          next[replaceIdx] = newIntents[0];
          setIntents(next);
        }
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [intents, isMultiple, setIntents],
  );

  const handleRequestDelete = useCallback((target: TFileIntent) => {
    setDeleteTarget(target);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleConfirmDelete = useCallback(
    () => {
      if (!deleteTarget) return;

      const idx = intents.indexOf(deleteTarget);
      if (idx === -1) {
        setDeleteTarget(null);
        return;
      }

      const next = [...intents];
      if (deleteTarget.mode === "keep") {
        next[idx] = {
          mode: "delete",
          id: deleteTarget.id,
          dataId: deleteTarget.dataId,
          url: deleteTarget.url,
          kind: deleteTarget.kind,
        };
        setIntents(next);
      } else if (deleteTarget.mode === "upload") {
        next.splice(idx, 1);
        setIntents(next);
      }
      setDeleteTarget(null);
    },
    [deleteTarget, intents, setIntents],
  );

  const handleKindChange = useCallback(
    (target: TFileIntent, kind: string) => {
      const idx = intents.indexOf(target);
      if (idx === -1) return;
      const nextKind = kind || undefined;
      const next = [...intents];
      if (target.mode === "keep") {
        next[idx] = { ...target, kind: nextKind };
      } else if (target.mode === "upload") {
        next[idx] = { ...target, kind: nextKind };
      } else {
        return;
      }
      setIntents(next);
    },
    [intents, setIntents],
  );

  const activeIntents = intents.filter(
    (intent): intent is TActiveFileIntent =>
      intent.mode === "keep" || intent.mode === "upload",
  );
  const showAddButton = isMultiple || activeIntents.length === 0;
  const kindError = meta.error === FILE_KIND_REQUIRED_ERROR;

  const label =
    typeof field.label === "string" && field.label ? field.label : field.name;

  return (
    <Box sx={{ gridColumn: "1 / -1", marginBottom: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={2}>
        {activeIntents.map((intent, index) => {
          const key =
            intent.mode === "keep"
              ? `keep-${intent.id}`
              : `upload-${index}-${intent.file.name}`;
          const showThumbnail =
            intent.mode === "keep" && requestId && documentInfo;
          return (
            <Stack key={key} gap={1} sx={{ width: 280 }}>
              {(showThumbnail || intent.mode === "upload") && (
                <Box
                  sx={{ position: "relative", width: 280, height: 280 }}
                  data-testid={
                    intent.mode === "upload"
                      ? `${field.name}-pending-upload`
                      : undefined
                  }
                >
                  {showThumbnail && intent.mode === "keep" && (
                    <FileContent
                      requestId={requestId!}
                      documentId={documentInfo!.documentId}
                      fileId={intent.dataId}
                      dataLink={intent.url}
                      fileInfo={{
                        mimeType: intent.mimeType,
                        originalName: intent.originalName,
                        kind: intent.kind,
                      }}
                    />
                  )}
                  {intent.mode === "upload" && (
                    <PendingUploadPreview fileName={intent.file.name} />
                  )}
                  <DeleteFileButton
                    onDelete={() => handleRequestDelete(intent)}
                    testId={`${field.name}-delete-${index}`}
                  />
                </Box>
              )}
              {kinds ? (
                <FormControl
                  variant="filled"
                  size="small"
                  fullWidth
                  required
                  error={kindError && !hasSelectedFileKind(intent)}
                >
                  <InputLabel id={`${field.name}-kind-label-${index}`}>
                    {FILE_KIND_LABEL}
                  </InputLabel>
                  <Select
                    variant="filled"
                    labelId={`${field.name}-kind-label-${index}`}
                    label={FILE_KIND_LABEL}
                    value={intent.kind ?? ""}
                    onChange={(e) =>
                      handleKindChange(intent, e.target.value as string)
                    }
                    inputProps={{
                      "data-testid": `${field.name}-kind-${index}`,
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {kinds.map((kind) => (
                      <MenuItem key={kind} value={kind}>
                        {kind}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : intent.kind ? (
                <Typography variant="body2" color="text.secondary">
                  {intent.kind}
                </Typography>
              ) : null}
            </Stack>
          );
        })}
        {showAddButton && (
          <Box
            sx={{
              width: 280,
              height: 280,
              border: "1px dashed #999",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              data-testid={`${field.name}-add`}
            >
              Add file
            </Button>
          </Box>
        )}
      </Stack>
      {meta.error && (
        <FormHelperText
          error
          sx={{ mt: 1 }}
          data-testid={`${field.name}-error`}
        >
          {meta.error}
        </FormHelperText>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple={isMultiple}
        style={{ display: "none" }}
        onChange={handleFileChange}
        data-testid={`${field.name}-file-input`}
      />
      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        onClose={handleCancelDelete}
        title={DELETE_CONFIRM_TITLE}
        description={DELETE_CONFIRM_MESSAGE}
        confirmAction={handleConfirmDelete}
        confirmText="Delete"
        declineText="Cancel"
        confirmButtonColor={mainTheme.palette.error.main}
      />
    </Box>
  );
});
